import {
  schemaCriarAgendamento,
  type AgendamentoNaLista,
  type EntradaCriarAgendamento,
} from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  Campo,
  ContadorTexto,
  Entrada,
  Etiqueta,
  Gaveta,
  ListaDefinicao,
  Modal,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { agendamentosApi } from '../../api/agendamentos'
import { chaves } from '../../api/chaves'
import { BuscadorLead, type LeadEscolhido } from '../../componentes/buscador-lead'
import { EtiquetaStatusAgendamento } from '../../componentes/etiquetas-dominio'
import { opcoesDeCatalogo, useCatalogoAtivo } from '../../ganchos/usar-catalogos'
import { useFormulario } from '../../ganchos/usar-formulario'
import { formatarDataHora, formatarTelefone, localParaInstante } from '../../utilitarios/formatar'

interface EstadoCriacao {
  leadUuid: string
  motivoId: string
  iniciaEm: string
  terminaEm: string
  diaInteiro: boolean
  titulo: string
  observacao: string
}

/** Modal de criação de agendamento (a partir do calendário ou da lista). */
export function ModalCriarAgendamento({
  aberto,
  onFechar,
  iniciaEmSugerido,
}: {
  aberto: boolean
  onFechar: () => void
  /** Valor de `datetime-local` pré-preenchido pelo clique no espaço vago. */
  iniciaEmSugerido?: string
}) {
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const motivos = useCatalogoAtivo('motivos-agendamento')
  const [lead, setLead] = useState<LeadEscolhido | null>(null)

  const formulario = useFormulario<EstadoCriacao>({
    leadUuid: '',
    motivoId: '',
    iniciaEm: iniciaEmSugerido ?? '',
    terminaEm: '',
    diaInteiro: false,
    titulo: '',
    observacao: '',
  })

  // O clique no espaço vago troca a hora sugerida enquanto o modal está fechado.
  if (aberto && iniciaEmSugerido && formulario.valores.iniciaEm === '') {
    formulario.definir('iniciaEm', iniciaEmSugerido)
  }

  async function submeter(evento: React.SyntheticEvent) {
    evento.preventDefault()
    if (!lead) {
      formulario.setErros({ leadUuid: 'Escolha o lead do compromisso.' })
      return
    }

    const concluiu = await formulario.enviar<EntradaCriarAgendamento>(
      schemaCriarAgendamento,
      (estado) => ({
        leadUuid: lead.uuid,
        motivoId: Number(estado.motivoId),
        iniciaEm: localParaInstante(estado.iniciaEm),
        terminaEm: estado.terminaEm ? localParaInstante(estado.terminaEm) : null,
        diaInteiro: estado.diaInteiro,
        titulo: estado.titulo.trim() || null,
        observacao: estado.observacao.trim() || null,
      }),
      async (corpo) => {
        await agendamentosApi.criar(corpo)
        void clienteConsulta.invalidateQueries({ queryKey: chaves.agendamentos.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
        avisos.sucesso('Agendamento criado', 'O lead passou para a situação Agendado.')
      },
    )

    if (concluiu) {
      setLead(null)
      formulario.reiniciar()
      onFechar()
    }
  }

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      travado={formulario.enviando}
      titulo="Novo agendamento"
      descricao="Criar um compromisso muda a situação do lead e define o próximo retorno."
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={formulario.enviando}>
            Cancelar
          </Botao>
          <Botao
            onClick={(evento) => void submeter(evento)}
            carregando={formulario.enviando}
            disabled={!lead || !formulario.valores.motivoId || !formulario.valores.iniciaEm}
          >
            Criar agendamento
          </Botao>
        </>
      }
    >
      <form onSubmit={submeter} noValidate className="flex flex-col gap-4">
        {formulario.erroGeral ? <Alerta tom="perigo">{formulario.erroGeral}</Alerta> : null}

        <Campo rotulo="Lead" obrigatorio erro={formulario.erros.leadUuid}>
          {(atributos) => (
            <BuscadorLead
              id={atributos.id}
              temErro={atributos.temErro}
              escolhido={lead}
              onEscolher={setLead}
            />
          )}
        </Campo>

        <Campo rotulo="Motivo" obrigatorio erro={formulario.erros.motivoId}>
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio="Selecione o motivo"
              value={formulario.valores.motivoId}
              temErro={atributos.temErro}
              opcoes={opcoesDeCatalogo(motivos.data?.dados)}
              onChange={(evento) => formulario.definir('motivoId', evento.target.value)}
            />
          )}
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Início"
            obrigatorio
            erro={formulario.erros.iniciaEm}
            dica="Precisa ser no futuro."
          >
            {(atributos) => (
              <Entrada
                {...atributos}
                type="datetime-local"
                value={formulario.valores.iniciaEm}
                temErro={atributos.temErro}
                onChange={(evento) => formulario.definir('iniciaEm', evento.target.value)}
              />
            )}
          </Campo>

          <Campo
            rotulo="Término"
            erro={formulario.erros.terminaEm}
            dica="Opcional — sem término, o compromisso é pontual."
          >
            {(atributos) => (
              <Entrada
                {...atributos}
                type="datetime-local"
                value={formulario.valores.terminaEm}
                min={formulario.valores.iniciaEm || undefined}
                onChange={(evento) => formulario.definir('terminaEm', evento.target.value)}
              />
            )}
          </Campo>
        </div>

        <Campo rotulo="Título" dica="Opcional. Aparece no evento da grade.">
          {(atributos) => (
            <Entrada
              {...atributos}
              value={formulario.valores.titulo}
              onChange={(evento) => formulario.definir('titulo', evento.target.value)}
            />
          )}
        </Campo>

        <Campo rotulo="Observação">
          {(atributos) => (
            <AreaTexto
              {...atributos}
              rows={3}
              value={formulario.valores.observacao}
              onChange={(evento) => formulario.definir('observacao', evento.target.value)}
            />
          )}
        </Campo>
      </form>
    </Modal>
  )
}

type AcaoAgendamento = 'concluir' | 'cancelar' | 'nao-compareceu' | 'reagendar'

/**
 * Gaveta de resumo do compromisso, com as ações do FL-09.
 *
 * Cada ação escreve no lead e gera interação — nenhuma é "só calendário". Por isso
 * `cancelar` e `nao-compareceu` pedem texto com o mínimo do RN-11: o que vai para a
 * timeline não pode ser vazio.
 */
export function GavetaAgendamento({
  agendamento,
  onFechar,
  podeGerenciar,
}: {
  agendamento: AgendamentoNaLista | null
  onFechar: () => void
  podeGerenciar: boolean
}) {
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const motivos = useCatalogoAtivo('motivos-agendamento')

  const [acao, setAcao] = useState<AcaoAgendamento | null>(null)
  const [texto, setTexto] = useState('')
  const [novaData, setNovaData] = useState('')
  const [novoMotivoId, setNovoMotivoId] = useState('')

  const invalidar = () => {
    void clienteConsulta.invalidateQueries({ queryKey: chaves.agendamentos.todos })
    void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
    void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
  }

  const executar = useMutation({
    mutationFn: async () => {
      if (!agendamento || !acao) return
      if (acao === 'concluir') {
        return agendamentosApi.concluir(agendamento.uuid, texto.trim() || undefined)
      }
      if (acao === 'cancelar') return agendamentosApi.cancelar(agendamento.uuid, texto.trim())
      if (acao === 'nao-compareceu') {
        return agendamentosApi.naoCompareceu(agendamento.uuid, texto.trim())
      }
      // `EntradaReagendar` é a **saída** do schema, onde `iniciaEm` já é `Date`
      // (`zInstante` transforma). `JSON.stringify` devolve o ISO-8601 em UTC que a
      // API espera — por isso o `Date` aqui, e não a string do input.
      return agendamentosApi.reagendar(agendamento.uuid, {
        iniciaEm: new Date(novaData),
        motivo: texto.trim(),
        ...(novoMotivoId ? { motivoId: Number(novoMotivoId) } : {}),
      })
    },
    onSuccess: () => {
      invalidar()
      avisos.sucesso('Agendamento atualizado')
      setAcao(null)
      setTexto('')
      setNovaData('')
      onFechar()
    },
    onError: (erro) => avisos.erro('Não foi possível concluir', erro.message),
  })

  if (!agendamento) return null

  const ativo = agendamento.status === 'AGENDADO'
  const exigeTexto = acao === 'cancelar' || acao === 'nao-compareceu' || acao === 'reagendar'
  const minimoTexto = acao === 'reagendar' ? 20 : 20
  const podeExecutar =
    acao !== null &&
    (!exigeTexto || texto.trim().length >= minimoTexto) &&
    (acao !== 'reagendar' || novaData !== '')

  return (
    <Gaveta
      aberta
      onFechar={onFechar}
      titulo={agendamento.lead.clienteNome}
      descricao={agendamento.motivo.titulo}
      largura="md"
      rodape={
        acao ? (
          <>
            <Botao variante="contorno" onClick={() => setAcao(null)} disabled={executar.isPending}>
              Voltar
            </Botao>
            <Botao
              variante={acao === 'concluir' ? 'sucesso' : 'primario'}
              onClick={() => executar.mutate()}
              carregando={executar.isPending}
              disabled={!podeExecutar}
            >
              Confirmar
            </Botao>
          </>
        ) : (
          <Botao variante="contorno" onClick={onFechar}>
            Fechar
          </Botao>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <EtiquetaStatusAgendamento status={agendamento.status} />
          {agendamento.vencido ? <Etiqueta tom="perigo">Vencido</Etiqueta> : null}
          <Etiqueta cor={agendamento.motivo.cor} tom="neutro">
            {agendamento.motivo.titulo}
          </Etiqueta>
        </div>

        <ListaDefinicao
          itens={[
            { rotulo: 'Início', valor: formatarDataHora(agendamento.iniciaEm) },
            ...(agendamento.terminaEm
              ? [{ rotulo: 'Término', valor: formatarDataHora(agendamento.terminaEm) }]
              : []),
            { rotulo: 'Responsável', valor: agendamento.responsavel.nome },
            { rotulo: 'Equipe', valor: agendamento.equipe?.titulo ?? '—' },
            { rotulo: 'Contato', valor: formatarTelefone(agendamento.lead.clienteContato) },
            ...(agendamento.observacao
              ? [{ rotulo: 'Observação', valor: agendamento.observacao }]
              : []),
            ...(agendamento.reagendadoDeUuid
              ? [{ rotulo: 'Origem', valor: 'Reagendamento de outro compromisso' }]
              : []),
          ]}
        />

        <Link
          to={`/leads/${agendamento.lead.uuid}`}
          className="text-sm font-medium text-marca-600 hover:underline"
        >
          Abrir o atendimento deste lead
        </Link>

        {!podeGerenciar ? (
          <Alerta tom="info">
            Seu perfil pode consultar a agenda, mas não alterar compromissos.
          </Alerta>
        ) : !ativo ? (
          <Alerta tom="info">
            Este compromisso já foi tratado. Concluir ou cancelar duas vezes é recusado pela API.
          </Alerta>
        ) : acao === null ? (
          <div className="flex flex-wrap gap-2 border-t border-borda pt-4">
            <Botao variante="sucesso" tamanho="sm" onClick={() => setAcao('concluir')}>
              Concluir
            </Botao>
            <Botao variante="contorno" tamanho="sm" onClick={() => setAcao('reagendar')}>
              Reagendar
            </Botao>
            <Botao variante="contorno" tamanho="sm" onClick={() => setAcao('nao-compareceu')}>
              Não compareceu
            </Botao>
            <Botao variante="fantasma" tamanho="sm" onClick={() => setAcao('cancelar')}>
              Cancelar compromisso
            </Botao>
          </div>
        ) : (
          <div className="flex flex-col gap-4 border-t border-borda pt-4">
            {acao === 'reagendar' && (
              <>
                <Campo rotulo="Nova data e hora" obrigatorio>
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      type="datetime-local"
                      value={novaData}
                      onChange={(evento) => setNovaData(evento.target.value)}
                    />
                  )}
                </Campo>
                <Campo rotulo="Trocar o motivo" dica="Opcional — mantém o motivo atual se vazio.">
                  {(atributos) => (
                    <Selecao
                      {...atributos}
                      vazio={`Manter ${agendamento.motivo.titulo}`}
                      value={novoMotivoId}
                      opcoes={opcoesDeCatalogo(motivos.data?.dados)}
                      onChange={(evento) => setNovoMotivoId(evento.target.value)}
                    />
                  )}
                </Campo>
              </>
            )}

            <Campo
              rotulo={
                acao === 'concluir'
                  ? 'O que aconteceu no compromisso'
                  : acao === 'reagendar'
                    ? 'Motivo do reagendamento'
                    : 'Motivo'
              }
              obrigatorio={exigeTexto}
              acessorio={<ContadorTexto valor={texto} minimo={minimoTexto} />}
              dica={
                acao === 'concluir'
                  ? 'Opcional aqui — o conteúdo da conversa costuma ir em uma interação própria.'
                  : 'Vai para a timeline do lead e não pode ser editado depois.'
              }
            >
              {(atributos) => (
                <AreaTexto
                  {...atributos}
                  rows={4}
                  value={texto}
                  onChange={(evento) => setTexto(evento.target.value)}
                />
              )}
            </Campo>

            {executar.isError ? <Alerta tom="perigo">{executar.error.message}</Alerta> : null}
          </div>
        )}
      </div>
    </Gaveta>
  )
}
